"use client";

/**
 * The next-prompt suggestion on a task's screen, as the pill's own TOP ROW
 * (spec 2026-09-20 one-composer-everywhere, R-F4 as the lead amended it). The
 * founder ruled on 08-30 that the suggestion is "not a separate box": on the
 * old command card it was the card's top row, capping the composer, and with
 * the Dock as the composer it caps the pill the same way. So this is a ROW of
 * the pill's surface: no border box, no ground, no margin of its own, only the
 * hairline the pill's rows already part on. Its top corners follow the pill's
 * (rounded-[22px], BashPill.tsx) so the hover wash never squares them off.
 *
 * Try writes the task's per-task prefill, the lane the shelf's take-back and
 * Studio's retry already ride: the pill's task pickers take the words
 * (PillTaskPickers) and the Dock opens the door for them (Dock.tsx). The
 * suggestion is read as the task layout's old card reads it, keyed by the task
 * it is handed. No suggestion, no row: the pill never wears an empty one.
 */
import { SparkleBurstFilledIcon } from "@/components/icons/SparkleBurstFilledIcon";
import { ArrowUpRightIcon } from "@/components/icons/ArrowUpRightIcon";
import { useComposerDraftStore } from "@/stores/composerDraftStore";
import { useTaskStore } from "@/stores/taskStore";

export function PillTryRow({
  taskId,
}: {
  /** The task in view, or null on the new-task page (no suggestion there). */
  taskId: string | null;
}) {
  const promptSuggestion = useTaskStore((s) => {
    const task = taskId ? s.tasks.get(taskId) : null;
    return task?.promptSuggestion ?? null;
  });
  const requestPrefill = useComposerDraftStore((s) => s.requestPrefill);
  if (!taskId || !promptSuggestion) return null;

  const accept = () => {
    requestPrefill(taskId, promptSuggestion);
    useTaskStore.getState()._mutateTasks((tasks) => {
      const t = tasks.get(taskId);
      if (t) t.promptSuggestion = null;
    });
  };

  return (
    <button
      type="button"
      onClick={accept}
      data-testid="prompt-suggestion-chip"
      aria-label="Try suggested prompt"
      className="bh-try group transition-bh-hover flex w-full cursor-pointer items-center gap-2.5 rounded-t-[22px] border-b border-border-subtle px-3.5 py-2 text-left hover:bg-bg-muted"
    >
      <SparkleBurstFilledIcon className="size-3.5 shrink-0 text-amber" />
      <span className="min-w-0 flex-1 truncate text-3.5 text-fg-default">
        {promptSuggestion}
      </span>
      <span className="transition-bh-hover inline-flex shrink-0 items-center gap-1 rounded-full border border-border-subtle bg-bg-muted px-2 py-0.5 text-3 font-medium text-fg-muted group-hover:bg-border-default group-hover:text-fg-default">
        Try
        <ArrowUpRightIcon className="size-3" strokeWidth="2.5" />
      </span>
    </button>
  );
}
